import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db, storage } from "@/lib/firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { claimUsername } from "@/data/username-claim";
import { searchLgsOSM } from "@/lib/osm-lgs";
import spellgatherLogo from "@/assets/brand/spellgather-logo.svg";

/* ----------------------------------------
   Types and constants (aligned with Settings.tsx)
----------------------------------------- */
type BracketId = 1 | 2 | 3 | 4 | 5;
const BRACKETS: { id: BracketId; title: string }[] = [
  { id: 1, title: "Bracket 1: Exhibition" },
  { id: 2, title: "Bracket 2: Core" },
  { id: 3, title: "Bracket 3: Upgraded" },
  { id: 4, title: "Bracket 4: Optimized" },
  { id: 5, title: "Bracket 5: cEDH" },
];
type CompetitiveChoice = "casual" | "competitive" | "ranked";
const COMPETITIVE_CHOICES: CompetitiveChoice[] = ["casual", "competitive", "ranked"];
type FollowPrivacy = "open" | "request" | "private";

const PLAY_FORMATS = [
  "Commander",
  "Canadian Highlander",
  "Modern",
  "Standard",
  "Pioneer",
  "Legacy",
  "Vintage",
  "Pauper",
  "Draft",
  "Sealed",
  "MTG Arena",
  "Explorer",
  "Historic",
  "Alchemy",
];
const MEETUP_TYPES = ["Private Match", "Public Match", "Local Game Store"];

type EmailPrefs = {
  offers: boolean;
  follows: boolean;
  dms: boolean;
  marketing: boolean;
};

type Visibility = {
  display_name: boolean;
  bio: boolean;
  location: boolean;
  play_formats: boolean;
  meetup_types: boolean;
  competitive_preference: boolean;
  commander_tiers: boolean;
  play_radius: boolean;
  trade_radius: boolean;
  available_for_trading: boolean;
  available_for_play: boolean;
  collection_browsing: boolean;
  wishlist_is_public: boolean;
  preferred_lgs: boolean;
};

type OnboardingState = {
  username: string;
  avatar_url: string | null;
  bio: string;

  // Core preferences
  preferences: {
    available_for_trading: boolean;
    available_for_play: boolean;
    collection_browsing: boolean;
    wishlist_is_public: boolean;
  };

  // Presence derived at save time
  follow_privacy: FollowPrivacy;

  // Visibility flags
  visibility: Visibility;

  // Location + LGS
  location: { lat: number | null; lng: number | null; city?: string; state?: string };
  preferred_lgs: string;
  autoLocationOnLogin: boolean; // app-side preference; also mirrored to user doc

  // Play preferences
  play_formats: string[];
  commander_brackets: BracketId[];
  competitive_preferences: CompetitiveChoice[];
  play_radius: number;
  trade_radius: number;

  // Email
  email_prefs: EmailPrefs;
};

const DEFAULT_VIS: Visibility = {
  display_name: true,
  bio: true,
  location: true,
  play_formats: true,
  meetup_types: true,
  competitive_preference: true,
  commander_tiers: true,
  play_radius: false,
  trade_radius: false,
  available_for_trading: true,
  available_for_play: true,
  collection_browsing: true,
  wishlist_is_public: false,
  preferred_lgs: true,
};

const DEFAULT_AVATAR = "/images/avatars/avatar_default.png";

const DEFAULT_STATE: OnboardingState = {
  username: "",
  avatar_url: DEFAULT_AVATAR,
  bio: "",

  preferences: {
    available_for_trading: true,
    available_for_play: true,
    collection_browsing: true,
    wishlist_is_public: false,
  },

  follow_privacy: "open",
  visibility: DEFAULT_VIS,

  location: { lat: null, lng: null },
  preferred_lgs: "",
  autoLocationOnLogin: false,

  play_formats: [],
  commander_brackets: [],
  competitive_preferences: [],
  play_radius: 10,
  trade_radius: 25,

  email_prefs: { offers: true, follows: true, dms: true, marketing: false },
};

/* ----------------------------------------
   LGS helpers
----------------------------------------- */
type WPNStore = {
  id?: string;
  name: string;
  address?: string;
  lat: number;
  lng: number;
  distanceKm?: number;
};

function kmDistance(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la = toRad(a.lat);
  const lb = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la) * Math.cos(lb) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 120);
}

/* ----------------------------------------
   Component
----------------------------------------- */
export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [s, setS] = useState<OnboardingState>(DEFAULT_STATE);
  const [showCongrats, setShowCongrats] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // LGS local state
  const [lgsLoading, setLgsLoading] = useState(false);
  const [lgs, setLgs] = useState<WPNStore[]>([]);
  const [lgsQuery, setLgsQuery] = useState("");
  const visibleLgs = useMemo(() => {
    const q = (lgsQuery || "").trim().toLowerCase();
    if (!q) return lgs;
    return lgs.filter((row) => (row.name.toLowerCase().includes(q) || (row.address || "").toLowerCase().includes(q)));
  }, [lgs, lgsQuery]);

  // Auth tracking
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => setUid(u?.uid ?? null));
    return unsub;
  }, []);

  // Preload user settings if any; skip if onboarding already completed
  useEffect(() => {
    (async () => {
      if (!uid) {
        setLoading(false);
        return;
      }
      const ref = doc(db, "users", uid);
      const snap = await getDoc(ref);
      const data = (snap.data() ?? {}) as any;

      const params = new URLSearchParams(window.location.search);
      const forceRepeat = params.get("repeat") === "1";
      if (data?.onboarding?.completedAt && !forceRepeat) {
        navigate("/", { replace: true });
        return;
      }

      const merged: OnboardingState = {
        ...DEFAULT_STATE,
        username: data.username ?? "",
        avatar_url:
          (data.avatar_url && String(data.avatar_url).trim()) || DEFAULT_AVATAR,
        bio: data.bio ?? "",
        preferences: {
          // Use nullish coalescing directly; avoid coercing undefined to false before ??
          available_for_trading:
            data?.preferences?.available_for_trading ?? DEFAULT_STATE.preferences.available_for_trading,
          available_for_play:
            data?.preferences?.available_for_play ?? DEFAULT_STATE.preferences.available_for_play,
          collection_browsing:
            data?.preferences?.collection_browsing ?? DEFAULT_STATE.preferences.collection_browsing,
          wishlist_is_public:
            data?.preferences?.wishlist_is_public ?? DEFAULT_STATE.preferences.wishlist_is_public,
        },
        follow_privacy: (data.follow_privacy as FollowPrivacy) ?? "open",
        visibility: { ...DEFAULT_VIS, ...(data.visibility ?? {}) },
        location: {
          lat: data?.location?.lat ?? null,
          lng: data?.location?.lng ?? null,
          city: data?.location?.city ?? "",
          state: data?.location?.state ?? "",
        },
        preferred_lgs: data?.preferred_lgs ?? "",
        autoLocationOnLogin: Boolean(data?.locationPreference?.autoOnLogin),
        play_formats: Array.isArray(data?.play_formats) ? data.play_formats : [],
        commander_brackets: Array.isArray(data?.commander_brackets)
          ? (data.commander_brackets as BracketId[])
          : (data?.commander_bracket ? [data.commander_bracket as BracketId] : []),
        competitive_preferences: Array.isArray(data?.competitive_preferences)
          ? (data.competitive_preferences as CompetitiveChoice[])
          : (data?.competitive_preference ? [data.competitive_preference as CompetitiveChoice] : []),
        play_radius: typeof data?.play_radius === "number" ? data.play_radius : 10,
        trade_radius: typeof data?.trade_radius === "number" ? data.trade_radius : 25,
        email_prefs: {
          offers: !!data?.email_prefs?.offers,
          follows: !!data?.email_prefs?.follows,
          dms: !!data?.email_prefs?.dms,
          marketing: !!data?.email_prefs?.marketing,
        },
      };
      setS(merged);

      // Ensure user doc exists
      if (!snap.exists()) {
        await setDoc(ref, { createdAt: serverTimestamp() }, { merge: true });
      }
      // Backfill missing/blank avatar on load
      if (!data?.avatar_url || String(data.avatar_url).trim() === "") {
        await updateDoc(ref, { avatar_url: DEFAULT_AVATAR, updatedAt: serverTimestamp() });
      }
      setLoading(false);
    })();
  }, [uid, navigate]);

  /* ----------------------------------------
     Saving helpers (partial and full)
  ----------------------------------------- */
  async function savePartial(patch: Partial<OnboardingState>) {
    if (!uid) return;
    const ref = doc(db, "users", uid);
    const update: Record<string, any> = { updatedAt: serverTimestamp() };

    if (patch.username !== undefined) update.username = patch.username;
    if (patch.bio !== undefined) update.bio = patch.bio;
    if (patch.avatar_url !== undefined) update.avatar_url = patch.avatar_url;

    if (patch.preferences) {
      update["preferences.available_for_trading"] = patch.preferences.available_for_trading;
      update["preferences.available_for_play"] = patch.preferences.available_for_play;
      update["preferences.collection_browsing"] = patch.preferences.collection_browsing;
      update["preferences.wishlist_is_public"] = patch.preferences.wishlist_is_public;
      // derived presence mirrors
      update["presence.show_in_marketplace"] = patch.preferences.available_for_trading;
      update["presence.show_in_community_stream"] = patch.preferences.available_for_play;
    }

    if (patch.follow_privacy) update.follow_privacy = patch.follow_privacy;

    if (patch.visibility) {
      for (const [k, v] of Object.entries(patch.visibility)) {
        update[`visibility.${k}`] = v;
      }
    }

    if (patch.location) {
      update["location.lat"] = patch.location.lat ?? null;
      update["location.lng"] = patch.location.lng ?? null;
      if ("city" in patch.location) update["location.city"] = patch.location.city ?? "";
      if ("state" in patch.location) update["location.state"] = patch.location.state ?? "";
    }

    if (patch.preferred_lgs !== undefined) update.preferred_lgs = patch.preferred_lgs;

    if (patch.autoLocationOnLogin !== undefined) {
      update["locationPreference.autoOnLogin"] = !!patch.autoLocationOnLogin;
    }

    if (patch.play_formats) update.play_formats = patch.play_formats;
    if (patch.commander_brackets) {
      update.commander_brackets = patch.commander_brackets;
      update.commander_bracket = patch.commander_brackets[0] ?? null; // legacy mirror
    }
    if (patch.competitive_preferences) {
      update.competitive_preferences = patch.competitive_preferences;
      update.competitive_preference = patch.competitive_preferences[0] ?? "casual"; // legacy mirror
    }
    if (patch.play_radius !== undefined) update.play_radius = patch.play_radius;
    if (patch.trade_radius !== undefined) update.trade_radius = patch.trade_radius;

    if (patch.email_prefs) {
      for (const [k, v] of Object.entries(patch.email_prefs)) {
        update[`email_prefs.${k}`] = v;
      }
    }

    await updateDoc(ref, update);
  }

  async function saveAllAndFinish() {
    if (!uid) return;
    setSaving(true);
    try {
      const ref = doc(db, "users", uid);
      await updateDoc(ref, {
        username: s.username,
        bio: s.bio,
        avatar_url: s.avatar_url || DEFAULT_AVATAR,

        preferences: { ...s.preferences },
        presence: {
          show_in_marketplace: !!s.preferences.available_for_trading,
          show_in_community_stream: !!s.preferences.available_for_play,
        },
        follow_privacy: s.follow_privacy,
        visibility: { ...s.visibility },

        location: { lat: s.location.lat ?? null, lng: s.location.lng ?? null, city: s.location.city ?? "", state: s.location.state ?? "" },
        preferred_lgs: s.preferred_lgs,
        "locationPreference.autoOnLogin": !!s.autoLocationOnLogin,

        play_formats: s.play_formats,
        commander_brackets: s.commander_brackets,
        commander_bracket: s.commander_brackets[0] ?? null, // legacy
        competitive_preferences: s.competitive_preferences,
        competitive_preference: s.competitive_preferences[0] ?? "casual",

        play_radius: s.play_radius,
        trade_radius: s.trade_radius,

        email_prefs: { ...s.email_prefs },

        onboarding: { completedAt: serverTimestamp(), version: 1 },
        updatedAt: serverTimestamp(),
      });

      // Flag walkthrough start (read in Dashboard/App)
      try { localStorage.setItem("sg:pendingTour", "1"); } catch {}

      setShowCongrats(true);
      setTimeout(() => navigate("/dashboard", { replace: true, state: { startTour: true } }), 1400);
    } finally {
      setSaving(false);
    }
  }

  /* ----------------------------------------
     Actions
  ----------------------------------------- */
  async function onUploadAvatar(file: File) {
    if (!uid) return;
    setSaving(true);
    try {
      const ext = (file.name.split(".").pop() || "png").toLowerCase();
      const path = `avatars/${uid}/onboarding.${ext}`;
      const fileRef = sRef(storage, path);
      await uploadBytes(fileRef, file, { contentType: file.type, cacheControl: "public, max-age=3600" });
      const url = await getDownloadURL(fileRef);
      setS((prev) => ({ ...prev, avatar_url: url }));
      await savePartial({ avatar_url: url });
      setMessage("Avatar updated");
      setTimeout(() => setMessage(null), 1500);
    } finally {
      setSaving(false);
    }
  }

  async function onUseMyLocation() {
    if (!navigator.geolocation) {
      setMessage("Geolocation not supported");
      setTimeout(() => setMessage(null), 1600);
      return;
    }
    setMessage("Getting location…");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setS((prev) => ({ ...prev, location: { ...prev.location, lat: latitude, lng: longitude } }));
        await savePartial({ location: { lat: latitude, lng: longitude } });
        setMessage("Location set ✓");
        setTimeout(() => setMessage(null), 1200);
      },
      () => {
        setMessage("Could not get location");
        setTimeout(() => setMessage(null), 1600);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  async function findNearbyLGS(radiusMiles = 100) {
    if (s.location.lat == null || s.location.lng == null) {
      setMessage("Set your location first");
      setTimeout(() => setMessage(null), 1500);
      return;
    }
    setLgsLoading(true);
    try {
      // Try OSM helper (project provides Wizards proxy in Settings; we’ll rely on OSM here)
      const osmRows = await searchLgsOSM(s.location.lat, s.location.lng, radiusMiles);
      const rows = (osmRows ?? []).map((r) => ({
        id: r.id,
        name: r.name,
        address: r.address,
        lat: r.lat,
        lng: r.lng,
        distanceKm: r.distanceKm ?? kmDistance({ lat: s.location.lat!, lon: s.location.lng! }, { lat: r.lat, lon: r.lng }),
      })) as WPNStore[];
      rows.sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0));
      setLgs(rows);
    } finally {
      setLgsLoading(false);
    }
  }

  async function selectLgs(store: WPNStore) {
    if (!uid) return;
    setS((prev) => ({ ...prev, preferred_lgs: store.name }));
    await savePartial({ preferred_lgs: store.name });
    // Upsert store doc (best-effort)
    try {
      const idBase = slugify(`${store.name}-${store.address || ""}-${store.lat.toFixed(4)}-${store.lng.toFixed(4)}`);
      const lgsRef = doc(db, "lgs", idBase);
      await setDoc(
        lgsRef,
        {
          name: store.name,
          address: store.address || "",
          lat: store.lat,
          lng: store.lng,
          source: store.id ? "wizards" : "osm",
          external_id: store.id || null,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          createdBy: uid,
        },
        { merge: true },
      );
    } catch {
      // ignore; user's preferred_lgs is already set
    }
  }

  async function ensureUsername() {
    if (!uid) return;
    const normalized = (s.username || "").trim().toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9_-]/g, "");
    if (!normalized) {
      setMessage("Pick a valid username");
      setTimeout(() => setMessage(null), 1600);
      return;
    }
    try {
      const u = await claimUsername(uid, normalized);
      setS((prev) => ({ ...prev, username: u }));
      await savePartial({ username: u });
      setMessage(`Username set to @${u}`);
    } catch (e: any) {
      setMessage(e?.message ?? "Could not set username");
    } finally {
      setTimeout(() => setMessage(null), 2000);
    }
  }

  const lastStepIndex = 6;
  const canNext = step !== 0 || !!s.username; // must claim username before proceeding

  // Location nag state
  const [locNagOpen, setLocNagOpen] = useState(false);
  const [locNagProceed, setLocNagProceed] = useState(false);

  function shouldBlockForLocation() {
    return (s.location.lat == null || s.location.lng == null);
  }

  function handleNext() {
    // After Location step (step >= 3), prompt once if no coords
    if (step >= 3 && shouldBlockForLocation() && !locNagProceed) {
      setLocNagOpen(true);
      return;
    }
    setStep((n) => Math.min(lastStepIndex, n + 1));
  }

  if (!uid) {
    return (
      <main className="max-w-3xl mx-auto p-6">
        <h1 className="text-white text-2xl font-bold mb-2">Welcome</h1>
        <p className="text-slate-300">Please sign in to continue.</p>
      </main>
    );
  }
  if (loading) {
    return (
      <main className="max-w-3xl mx-auto p-6">
        <p className="text-slate-300">Loading…</p>
      </main>
    );
  }

  const stepTitles = [
    "Username",
    "Profile",
    "Availability",
    "Location & LGS",
    "Play Preferences",
    "Community & Email",
    "Review",
  ];
  return (
    <main className="max-w-3xl mx-auto p-6">
      <header className="mb-6">
        <div className="mb-4 flex justify-center">
          <img
            src="/images/spellgather-logo.png"
            alt="Spellgather"
            className="h-8 sm:h-10 opacity-95"
          />
        </div>
        <h1 className="text-white text-2xl font-extrabold">Welcome to Spellgather!</h1>
        <p className="text-slate-300">
          Thanks for signing up. Next, we’ll collect your preferences to ensure the best experience.
        </p>
      </header>

      <Stepper step={step} labels={stepTitles} />

      {/* Step 0: Username (always visible first; locked once claimed) */}
      {step === 0 && (
        <section className="mt-6 space-y-4">
          <h2 className="text-white text-xl font-bold">Choose your username</h2>
          <div className="flex gap-2 items-stretch">
            <input
              className={[
                "border rounded px-3 py-2 flex-1",
                s.username ? "bg-black/30 text-slate-400 cursor-not-allowed border-gray-700" : "text-black",
              ].join(" ")}
              placeholder="choose-a-name"
              value={s.username}
              onChange={(e) => setS((p) => ({ ...p, username: e.target.value }))}
              disabled={!!s.username}
              readOnly={!!s.username}
            />
            {s.username ? (
              <span className="px-3 py-2 rounded-md border border-gray-700 text-slate-300 bg-black/40 select-none">
                Locked
              </span>
            ) : (
              <button
                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-500"
                onClick={() => void ensureUsername()}
              >
                Reserve
              </button>
            )}
          </div>
          <div className="text-xs text-slate-400">
            Usernames are permanent. Use lowercase letters, numbers, underscore, or dash.
          </div>
        </section>
      )}

      {/* Step 1: Profile */}
      {step === 1 && (
        <section className="mt-6 space-y-6">
          <h2 className="text-white text-xl font-bold">Section 1: Profile</h2>
          {/* Show locked username for clarity */}
          <label className="block">
            <span className="text-slate-200 text-sm">Username</span>
            <input
              className="mt-1 w-full rounded-md bg-black/30 text-slate-400 border border-gray-700 px-3 py-2 cursor-not-allowed"
              value={s.username ? `@${s.username}` : ""}
              disabled
              readOnly
            />
          </label>

          {/* Avatar upload (same interaction as Settings: pick a file to replace) */}
          <div className="flex items-center gap-4">
            <AvatarPreview src={s.avatar_url} />
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-slate-200 text-sm">
                <span>Avatar</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => e.currentTarget.files?.[0] && void onUploadAvatar(e.currentTarget.files[0])}
                />
              </label>
              {s.avatar_url && s.avatar_url !== DEFAULT_AVATAR && (
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded border border-gray-700 text-slate-300 hover:bg-white/5"
                  onClick={async () => {
                    const fallback = DEFAULT_AVATAR;
                    setS((p) => ({ ...p, avatar_url: fallback }));
                    await savePartial({ avatar_url: fallback });
                    setMessage("Avatar reset");
                    setTimeout(() => setMessage(null), 1200);
                  }}
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          <label className="block">
            <span className="text-slate-200 text-sm">Bio</span>
            <textarea
              className="mt-1 w-full rounded-md bg-black/40 text-white border border-gray-700 px-3 py-2"
              rows={4}
              value={s.bio}
              onChange={(e) => setS((p) => ({ ...p, bio: e.target.value }))}
              onBlur={() => void savePartial({ bio: s.bio })}
            />
          </label>

          <div className="flex items-center justify-between">
            <label className="text-slate-200 text-sm">Show bio on profile</label>
            <Switch
              checked={s.visibility.bio}
              onChange={async (v) => {
                setS((p) => ({ ...p, visibility: { ...p.visibility, bio: v } }));
                await savePartial({ visibility: { ...s.visibility, bio: v } });
              }}
            />
          </div>
        </section>
      )}

      {/* Step 2: Availability */}
      {step === 2 && (
        <section className="mt-6 space-y-6">
          <h2 className="text-white text-xl font-bold">Section 2: Availability</h2>

          {/* Safety Disclaimer & Community Guidelines */}
          <div className="rounded-md border border-amber-500/40 bg-amber-400/10 p-4 text-amber-100">
            <h3 className="font-semibold text-amber-300">Safety Disclaimer & Community Guidelines</h3>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-amber-100/90">
              <li>
                Always meet in public, well-lit areas such as game stores, cafés, libraries, or other designated
                “safe zones.”
              </li>
              <li>
                Avoid sharing personal or sensitive information (like your home address) until you’ve established trust.
              </li>
              <li>
                Bring a friend if possible and let someone you trust know where you are meeting and when.
              </li>
              <li>
                Trust your instincts. If something feels unsafe or uncomfortable, you are not obligated to continue with
                the meetup.
              </li>
            </ul>
            <p className="mt-3 text-amber-200/90">
              Spellgather is not responsible for the conduct of individual users or the outcome of meetups. By using
              this feature, you acknowledge that participation is voluntary and at your own risk.
            </p>
            <p className="mt-2 text-amber-200/90">
              By continuing, you agree to use the app responsibly, respect other community members, and prioritize your
              own safety.
            </p>
          </div>

          <ToggleRow
            label="Available for Trade"
            checked={s.preferences.available_for_trading}
            onChange={async (v) => {
              const next = { ...s.preferences, available_for_trading: v };
              setS((p) => ({ ...p, preferences: next }));
              await savePartial({ preferences: next });
            }}
          />
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-200">Show “Available for Trade” on profile</span>
            <Switch
              checked={s.visibility.available_for_trading}
              onChange={async (v) => {
                setS((p) => ({ ...p, visibility: { ...p.visibility, available_for_trading: v } }));
                await savePartial({ visibility: { ...s.visibility, available_for_trading: v } });
              }}
            />
          </div>

          <ToggleRow
            label="Available for Play"
            checked={s.preferences.available_for_play}
            onChange={async (v) => {
              const next = { ...s.preferences, available_for_play: v };
              setS((p) => ({ ...p, preferences: next }));
              await savePartial({ preferences: next });
            }}
          />
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-200">Show “Available for Play” on profile</span>
            <Switch
              checked={s.visibility.available_for_play}
              onChange={async (v) => {
                setS((p) => ({ ...p, visibility: { ...p.visibility, available_for_play: v } }));
                await savePartial({ visibility: { ...s.visibility, available_for_play: v } });
              }}
            />
          </div>
        </section>
      )}

      {/* Step 3: Location & LGS */}
      {step === 3 && (
        <section className="mt-6 space-y-6">
          <h2 className="text-white text-xl font-bold">Section 3: Location & Preferred LGS</h2>

          <div className="flex items-center justify-between">
            <span className="text-slate-200">Show my location on profile</span>
            <Switch
              checked={s.visibility.location}
              onChange={async (v) => {
                setS((p) => ({ ...p, visibility: { ...p.visibility, location: v } }));
                await savePartial({ visibility: { ...s.visibility, location: v } });
              }}
            />
          </div>

          <div className="flex items-center gap-3">
            <button className="px-3 py-2 rounded-md border border-gray-700 text-slate-200 hover:bg-white/5" onClick={() => void onUseMyLocation()}>
              Use my location
            </button>
            <span className="text-sm text-slate-300" aria-live="polite">
              {s.location.lat && s.location.lng ? "coords set ✓" : "no location set"}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-slate-200">Always get my location on login</span>
            <Switch
              checked={s.autoLocationOnLogin}
              onChange={async (v) => {
                setS((p) => ({ ...p, autoLocationOnLogin: v }));
                await savePartial({ autoLocationOnLogin: v });
              }}
            />
          </div>

          <div className="grid gap-2">
            <label className="text-slate-200 text-sm">Preferred LGS</label>
            <div className="flex gap-2">
              <input
                className="border rounded px-3 py-2 text-black flex-1"
                placeholder="Type to filter results or enter a store"
                value={s.preferred_lgs}
                onChange={(e) => {
                  const v = e.target.value;
                  setS((p) => ({ ...p, preferred_lgs: v }));
                  setLgsQuery(v);
                }}
              />
              <button
                className="px-3 py-2 rounded-md border border-gray-700 text-slate-200 hover:bg-white/5"
                onClick={() => void findNearbyLGS(100)}
              >
                {lgsLoading ? "Searching…" : "Find nearby"}
              </button>
            </div>

            {(lgsLoading || lgs.length > 0) && (
              <div className="max-h-72 overflow-auto rounded border border-gray-800 mt-2">
                {visibleLgs.map((row, i) => (
                  <button
                    key={`${row.id ?? row.name}-${i}`}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-black/20 border-b border-gray-800 last:border-b-0"
                    onClick={() => void selectLgs(row)}
                    title={row.address ?? row.name}
                  >
                    <div>
                      <div className="font-semibold text-yellow-300">{row.name}</div>
                      <div className="text-xs text-white/90">{row.address || "Nearby store"}</div>
                    </div>
                    <div className="text-xs text-white/80">
                      {typeof row.distanceKm === "number" ? `${(row.distanceKm * 0.621371).toFixed(1)} mi` : "—"}
                    </div>
                  </button>
                ))}
                {!lgsLoading && !visibleLgs.length && <div className="px-3 py-2 text-sm text-slate-400">No results.</div>}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Step 4: Play Preferences */}
      {step === 4 && (
        <section className="mt-6 space-y-6">
          <h2 className="text-white text-xl font-bold">Section 4: Play Preferences</h2>

          <div className="flex items-center justify-between">
            <span className="text-slate-200">Show preferred formats</span>
            <Switch
              checked={s.visibility.play_formats}
              onChange={async (v) => {
                setS((p) => ({ ...p, visibility: { ...p.visibility, play_formats: v } }));
                await savePartial({ visibility: { ...s.visibility, play_formats: v } });
              }}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {PLAY_FORMATS.map((fmt) => {
              const selected = s.play_formats.includes(fmt);
              return (
                <button
                  key={fmt}
                  aria-pressed={selected}
                  className={`px-3 py-2 rounded text-sm ${
                    selected ? "bg-blue-600/30 border border-blue-400 text-white" : "bg-black/30 border border-gray-700 text-slate-200"
                  }`}
                  onClick={async () => {
                    const next = selected ? s.play_formats.filter((x) => x !== fmt) : [...s.play_formats, fmt];
                    setS((p) => ({ ...p, play_formats: next }));
                    await savePartial({ play_formats: next });
                  }}
                >
                  {fmt}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-slate-200">Show commander brackets</span>
            <Switch
              checked={s.visibility.commander_tiers}
              onChange={async (v) => {
                setS((p) => ({ ...p, visibility: { ...p.visibility, commander_tiers: v } }));
                await savePartial({ visibility: { ...s.visibility, commander_tiers: v } });
              }}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            {BRACKETS.map((b) => {
              const selected = s.commander_brackets.includes(b.id);
              return (
                <div key={b.id} className={`p-3 rounded border ${selected ? "border-amber-400 bg-black/30" : "border-gray-700 bg-black/10"}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{b.title}</div>
                    <Switch
                      checked={selected}
                      onChange={async (v) => {
                        const next = v ? [...s.commander_brackets, b.id] as BracketId[] : (s.commander_brackets.filter((x) => x !== b.id) as BracketId[]);
                        setS((p) => ({ ...p, commander_brackets: next }));
                        await savePartial({ commander_brackets: next });
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-slate-200">Show competitive preferences</span>
            <Switch
              checked={s.visibility.competitive_preference}
              onChange={async (v) => {
                setS((p) => ({ ...p, visibility: { ...p.visibility, competitive_preference: v } }));
                await savePartial({ visibility: { ...s.visibility, competitive_preference: v } });
              }}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {COMPETITIVE_CHOICES.map((opt) => {
              const selected = s.competitive_preferences.includes(opt);
              return (
                <button
                  key={opt}
                  className={`px-3 py-2 rounded text-sm ${
                    selected ? "bg-blue-600/30 border border-blue-400 text-white" : "bg-black/30 border border-gray-700 text-slate-200"
                  }`}
                  aria-pressed={selected}
                  onClick={async () => {
                    const next = selected ? s.competitive_preferences.filter((x) => x !== opt) : [...s.competitive_preferences, opt];
                    setS((p) => ({ ...p, competitive_preferences: next }));
                    await savePartial({ competitive_preferences: next });
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-slate-200">Play radius (mi)</label>
                <Switch
                  checked={s.visibility.play_radius}
                  onChange={async (v) => {
                    setS((p) => ({ ...p, visibility: { ...p.visibility, play_radius: v } }));
                    await savePartial({ visibility: { ...s.visibility, play_radius: v } });
                  }}
                />
              </div>
              <input
                type="number"
                className="mt-1 border rounded px-3 py-2 text-black w-full"
                value={s.play_radius}
                onChange={(e) => setS((p) => ({ ...p, play_radius: parseInt(e.target.value || "0", 10) }))}
                onBlur={() => void savePartial({ play_radius: s.play_radius })}
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-slate-200">Trade radius (mi)</label>
                <Switch
                  checked={s.visibility.trade_radius}
                  onChange={async (v) => {
                    setS((p) => ({ ...p, visibility: { ...p.visibility, trade_radius: v } }));
                    await savePartial({ visibility: { ...s.visibility, trade_radius: v } });
                  }}
                />
              </div>
              <input
                type="number"
                className="mt-1 border rounded px-3 py-2 text-black w-full"
                value={s.trade_radius}
                onChange={(e) => setS((p) => ({ ...p, trade_radius: parseInt(e.target.value || "0", 10) }))}
                onBlur={() => void savePartial({ trade_radius: s.trade_radius })}
              />
            </div>
          </div>
        </section>
      )}

      {/* Step 5: Community & Email */}
      {step === 5 && (
        <section className="mt-6 space-y-6">
          <h2 className="text-white text-xl font-bold">Section 5: Community & Email</h2>

          <div className="grid gap-2">
            <label className="text-slate-200">Followers</label>
            <select
              className="border rounded px-3 py-2 text-black w-full"
              value={s.follow_privacy}
              onChange={async (e) => {
                const v = e.target.value as FollowPrivacy;
                setS((p) => ({ ...p, follow_privacy: v }));
                await savePartial({ follow_privacy: v });
              }}
            >
              <option value="open">Public — Anyone can follow</option>
              <option value="request">Review — Approve follow requests</option>
              <option value="private">No followers — Private</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-slate-200">Allow collection browsing</div>
            <Switch
              checked={s.preferences.collection_browsing}
              onChange={async (v) => {
                const next = { ...s.preferences, collection_browsing: v };
                setS((p) => ({ ...p, preferences: next }));
                await savePartial({ preferences: next });
              }}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="text-slate-200">Public wishlist</div>
            <Switch
              checked={s.preferences.wishlist_is_public}
              onChange={async (v) => {
                const next = { ...s.preferences, wishlist_is_public: v };
                setS((p) => ({ ...p, preferences: next }));
                await savePartial({ preferences: next });
              }}
            />
          </div>

          <div className="rounded-lg border border-gray-800 bg-black/20 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-slate-200">Email me about offers</span>
              <Switch
                checked={s.email_prefs.offers}
                onChange={async (v) => {
                  const next = { ...s.email_prefs, offers: v };
                  setS((p) => ({ ...p, email_prefs: next }));
                  await savePartial({ email_prefs: next });
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-200">Email me about new followers</span>
              <Switch
                checked={s.email_prefs.follows}
                onChange={async (v) => {
                  const next = { ...s.email_prefs, follows: v };
                  setS((p) => ({ ...p, email_prefs: next }));
                  await savePartial({ email_prefs: next });
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-200">Email me about direct messages</span>
              <Switch
                checked={s.email_prefs.dms}
                onChange={async (v) => {
                  const next = { ...s.email_prefs, dms: v };
                  setS((p) => ({ ...p, email_prefs: next }));
                  await savePartial({ email_prefs: next });
                }}
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-200">News & updates</span>
              <Switch
                checked={s.email_prefs.marketing}
                onChange={async (v) => {
                  const next = { ...s.email_prefs, marketing: v };
                  setS((p) => ({ ...p, email_prefs: next }));
                  await savePartial({ email_prefs: next });
                }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Step 6: Review */}
      {step === lastStepIndex && (
        <section className="mt-6 space-y-4">
          <h2 className="text-white text-xl font-bold">Review & Confirm</h2>
          <Summary s={s} />
        </section>
      )}

      {/* Footer controls */}
      <footer className="mt-8 flex items-center justify-between gap-3">
        <span className="text-sm text-slate-400" aria-live="polite">{message}</span>
        <div className="flex items-center gap-3">
          <button
            className="px-3 py-2 rounded-md border border-gray-700 text-slate-200 hover:bg-white/5 disabled:opacity-50"
            onClick={() => setStep((n) => Math.max(0, n - 1))}
            disabled={saving || step === 0}
          >
            Back
          </button>
          {step < lastStepIndex ? (
            <button
              className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
              onClick={handleNext}
              disabled={saving || !canNext}
            >
              Next
            </button>
          ) : (
            <button
              className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
              onClick={() => {
                if (shouldBlockForLocation() && !locNagProceed) {
                  setLocNagOpen(true);
                  return;
                }
                void saveAllAndFinish();
              }}
              disabled={saving}
            >
              Finish
            </button>
          )}
        </div>
      </footer>

      {/* Branded final modal */}
      {showCongrats && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70">
          <div className="relative w-[min(90vw,520px)] rounded-2xl border border-sky-800 bg-[#0f1720] p-6 shadow-2xl text-center">
            <img
              src="/images/spellgather-logo.png"
              alt="Spellgather"
              className="absolute top-3 right-3 h-6 opacity-90"
            />
            <div className="mx-auto mb-3 inline-grid place-items-center rounded-full border border-sky-800 bg-sky-900/20 w-10 h-10 text-sky-300">
              ✦
            </div>
            <h3 className="text-white text-xl font-extrabold mb-1 tracking-tight">You’re all set!</h3>
            <p className="text-sky-300">Let’s gather some spells!</p>
          </div>
        </div>
      )}

      {/* Missing location nag modal */}
      {locNagOpen && (
        <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" onClick={() => setLocNagOpen(false)}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="absolute inset-0 grid place-items-center p-4" onClick={(e) => e.stopPropagation()}>
            <div className="w-[min(92vw,520px)] rounded-2xl border border-sky-800 bg-[#0c1222] text-white shadow-2xl">
              <div className="p-5 border-b border-sky-800/50">
                <h3 className="text-lg font-bold">Set your location for the best experience</h3>
              </div>
              <div className="p-5 space-y-3 text-slate-200">
                <p>
                  Your approximate location enables nearby players, local listings, play opportunities,
                  and store recommendations. We store coordinates in an obfuscated form and only use them to
                  power features.
                </p>
                <p className="text-slate-300">
                  You can disable location-based features anytime by toggling “Trade” and/or “Play” in the avatar menu.
                </p>
              </div>
              <div className="p-5 pt-0 flex flex-wrap gap-2 justify-end">
                <button
                  className="btn-pixel btn-pixel-secondary"
                  onClick={() => {
                    setLocNagProceed(true);
                    setLocNagOpen(false);
                    // Advance or finish after consent to continue without location
                    if (step < lastStepIndex) {
                      setStep((n) => Math.min(lastStepIndex, n + 1));
                    } else {
                      void saveAllAndFinish();
                    }
                  }}
                >
                  Continue without location
                </button>
                <button
                  className="btn-pixel btn-pixel-primary"
                  onClick={async () => {
                    await onUseMyLocation();
                    setLocNagOpen(false);
                  }}
                >
                  Use my location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

/* ----------------------------------------
   Small UI helpers (no external deps)
----------------------------------------- */
function Stepper({ step, labels }: { step: number; labels: string[] }) {
  return (
    <ol className="flex items-center gap-2 text-sm text-slate-300 overflow-x-auto">
      {labels.map((label, i) => (
        <li key={label} className="flex items-center gap-2 shrink-0">
          <div
            className={[
              "w-7 h-7 inline-grid place-items-center rounded-full border",
              i <= step ? "border-blue-500 bg-blue-600/20 text-blue-200" : "border-gray-700 bg-black/40 text-slate-400",
            ].join(" ")}
          >
            {i + 1}
          </div>
          <span className={i === step ? "text-white font-semibold" : ""}>{label}</span>
          {i < labels.length - 1 && <span className="mx-2 h-px w-8 bg-gray-700" aria-hidden />}
        </li>
      ))}
    </ol>
  );
}

function AvatarPreview({ src }: { src: string | null }) {
  return (
    <div className="w-16 h-16 rounded-full overflow-hidden border border-gray-700 bg-black/40">
      {src ? <img src={src} alt="Avatar" className="w-full h-full object-cover" /> : (
        <div className="w-full h-full grid place-items-center text-slate-500 text-xs">No avatar</div>
      )}
    </div>
  );
}

function Switch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void | Promise<void> }) {
  return (
    <label className="inline-flex items-center gap-3 cursor-pointer">
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => void onChange(e.target.checked)}
      />
      <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${checked ? "bg-amber-400" : "bg-gray-600"}`}>
        <span className={`inline-block h-5 w-5 transform rounded-full bg-black transition ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </span>
    </label>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void | Promise<void>;
}) {
  return (
    <label className="flex items-center justify-between gap-4">
      <span className="text-slate-200">{label}</span>
      <Switch checked={checked} onChange={onChange} />
    </label>
  );
}

function Summary({ s }: { s: OnboardingState }) {
  return (
    <div className="rounded-lg border border-gray-800 bg-black/30 p-4 text-slate-200 text-sm space-y-3">
      <div><span className="text-white font-semibold">Username:</span> @{s.username || "—"}</div>
      <div><span className="text-white font-semibold">Trade:</span> {s.preferences.available_for_trading ? "On" : "Off"} • <span className="text-white font-semibold">Play:</span> {s.preferences.available_for_play ? "On" : "Off"}</div>
      <div><span className="text-white font-semibold">Location:</span> {s.location.lat && s.location.lng ? "Set" : "Not set"} • <span className="text-white font-semibold">LGS:</span> {s.preferred_lgs || "—"}</div>
      <div><span className="text-white font-semibold">Formats:</span> {s.play_formats.join(", ") || "—"}</div>
      <div><span className="text-white font-semibold">Commander:</span> {s.commander_brackets.length ? `${s.commander_brackets.length} selected` : "—"}</div>
      <div><span className="text-white font-semibold">Competitive:</span> {s.competitive_preferences.join(", ") || "—"}</div>
      <div><span className="text-white font-semibold">Radii:</span> Play {s.play_radius} mi • Trade {s.trade_radius} mi</div>
      <div><span className="text-white font-semibold">Followers:</span> {s.follow_privacy}</div>
      <div><span className="text-white font-semibold">Email:</span> offers {s.email_prefs.offers ? "on" : "off"}, follows {s.email_prefs.follows ? "on" : "off"}, DMs {s.email_prefs.dms ? "on" : "off"}, news {s.email_prefs.marketing ? "on" : "off"}</div>
    </div>
  );
}
